import Phaser from 'phaser';

interface TilesetConfig {
  category: string;
  tiles: {
    key: string;
    path: string;
    variants?: number;
  }[];
}

export class TilesetManager {
  private scene: Phaser.Scene;
  private loadedTextures: Set<string>;
  private tileVariants: Map<string, number>;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.loadedTextures = new Set();
    this.tileVariants = new Map();
  }

  public preloadTilesets(): void {
    console.log('🎨 TilesetManager: Preloading tilesets...');

    const tilesets: TilesetConfig[] = [
       {
         category: 'ocean',
         tiles: [
           { key: 'ocean_animation', path: '/assets/tilesets/ocean/ocean_animation_frame_', variants: 5 },
           { key: 'ocean', path: '/assets/tilesets/ocean/ocean', variants: 3 },
         ],
       },
       {
         category: 'island',
         tiles: [
           // Generic tiles used for all biomes
           { key: 'beach_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'beach_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'beach_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'beach_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Beach shoreline variants
           { key: 'beach_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'beach_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'beach_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'beach_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'beach_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'beach_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'beach_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'beach_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'beach_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'beach_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'beach_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'beach_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Forest biome - using generic
           { key: 'forest_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'forest_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'forest_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'forest_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Forest shoreline variants
           { key: 'forest_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'forest_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'forest_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'forest_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'forest_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'forest_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'forest_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'forest_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'forest_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'forest_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'forest_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'forest_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Mountain biome - using generic
           { key: 'mountain_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'mountain_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'mountain_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'mountain_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Mountain shoreline variants
           { key: 'mountain_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'mountain_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'mountain_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'mountain_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'mountain_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'mountain_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'mountain_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'mountain_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'mountain_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'mountain_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'mountain_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'mountain_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Jungle biome - using generic
           { key: 'jungle_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'jungle_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'jungle_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'jungle_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Jungle shoreline variants
           { key: 'jungle_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'jungle_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'jungle_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'jungle_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'jungle_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'jungle_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'jungle_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'jungle_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'jungle_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'jungle_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'jungle_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'jungle_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Desert biome - using generic
           { key: 'desert_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'desert_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'desert_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'desert_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Desert shoreline variants
           { key: 'desert_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'desert_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'desert_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'desert_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'desert_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'desert_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'desert_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'desert_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'desert_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'desert_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'desert_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'desert_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Volcanic biome - using generic
           { key: 'volcanic_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'volcanic_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'volcanic_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'volcanic_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Volcanic shoreline variants
           { key: 'volcanic_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'volcanic_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'volcanic_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'volcanic_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'volcanic_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'volcanic_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'volcanic_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'volcanic_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'volcanic_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'volcanic_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'volcanic_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'volcanic_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Winter biome - using generic
           { key: 'winter_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'winter_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'winter_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'winter_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Winter shoreline variants
           { key: 'winter_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'winter_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'winter_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'winter_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'winter_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'winter_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'winter_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'winter_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'winter_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'winter_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'winter_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'winter_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
           // Mixed biome - using generic
           { key: 'mixed_shore', path: '/assets/tilesets/island/generic/snow_rock.png' },
           { key: 'mixed_main', path: '/assets/tilesets/island/generic/snow_ground.png' },
           { key: 'mixed_accent', path: '/assets/tilesets/island/generic/snow_patch.png' },
           { key: 'mixed_feature', path: '/assets/tilesets/island/generic/ice_ground.png' },
           // Mixed shoreline variants
           { key: 'mixed_shore_n', path: '/assets/tilesets/island/generic/shoreline/shore_n.png' },
           { key: 'mixed_shore_e', path: '/assets/tilesets/island/generic/shoreline/shore_e.png' },
           { key: 'mixed_shore_s', path: '/assets/tilesets/island/generic/shoreline/shore_s.png' },
           { key: 'mixed_shore_w', path: '/assets/tilesets/island/generic/shoreline/shore_w.png' },
           { key: 'mixed_shore_ne', path: '/assets/tilesets/island/generic/shoreline/shore_ne.png' },
           { key: 'mixed_shore_se', path: '/assets/tilesets/island/generic/shoreline/shore_se.png' },
           { key: 'mixed_shore_sw', path: '/assets/tilesets/island/generic/shoreline/shore_sw.png' },
           { key: 'mixed_shore_nw', path: '/assets/tilesets/island/generic/shoreline/shore_nw.png' },
           { key: 'mixed_shore_inner_ne', path: '/assets/tilesets/island/generic/shoreline/shore_inner_ne.png' },
           { key: 'mixed_shore_inner_se', path: '/assets/tilesets/island/generic/shoreline/shore_inner_se.png' },
           { key: 'mixed_shore_inner_sw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_sw.png' },
           { key: 'mixed_shore_inner_nw', path: '/assets/tilesets/island/generic/shoreline/shore_inner_nw.png' },
         ],
       },
      {
        category: 'structures',
        tiles: [
          { key: 'dock', path: '/assets/tilesets/structures/dock.png' },
          { key: 'building', path: '/assets/tilesets/structures/building.png' },
          { key: 'wall', path: '/assets/tilesets/structures/wall.png' },
        ],
      },
      {
        category: 'special',
        tiles: [
          { key: 'whirlpool', path: '/assets/tilesets/special/whirlpool.png' },
          { key: 'rift', path: '/assets/tilesets/special/rift.png' },
          { key: 'fishing_spot', path: '/assets/tilesets/special/fishing_spot.png' },
        ],
      },
       {
         category: 'ocean_decor',
         tiles: [
           { key: 'ocean_rock_01', path: '/assets/tilesets/ocean_decor/ocean_rock_01.png' },
           { key: 'ocean_rock_02', path: '/assets/tilesets/ocean_decor/ocean_rock_02.png' },
           { key: 'ocean_rock_03', path: '/assets/tilesets/ocean_decor/ocean_rock_03.png' },
           { key: 'ocean_rock_04', path: '/assets/tilesets/ocean_decor/ocean_rock_04.png' },
           { key: 'ocean_rock_05', path: '/assets/tilesets/ocean_decor/ocean_rock_05.png' },
           { key: 'ocean_rock_06', path: '/assets/tilesets/ocean_decor/ocean_rock_06.png' },
           { key: 'ocean_rock_07', path: '/assets/tilesets/ocean_decor/ocean_rock_07.png' },
           { key: 'ocean_rock_08', path: '/assets/tilesets/ocean_decor/ocean_rock_08.png' },
           { key: 'ocean_rock_09', path: '/assets/tilesets/ocean_decor/ocean_rock_09.png' },
           { key: 'ocean_rock_10', path: '/assets/tilesets/ocean_decor/ocean_rock_10.png' },
           { key: 'ocean_rock_11', path: '/assets/tilesets/ocean_decor/ocean_rock_11.png' },
           { key: 'ocean_rock_12', path: '/assets/tilesets/ocean_decor/ocean_rock_12.png' },
           { key: 'fish_1_frame_1', path: '/assets/tilesets/ocean_decor/fish_1/fish_1_frame_1.png' },
           { key: 'fish_1_frame_2', path: '/assets/tilesets/ocean_decor/fish_1/fish_1_frame_2.png' },
           { key: 'fish_1_frame_3', path: '/assets/tilesets/ocean_decor/fish_1/fish_1_frame_3.png' },
           { key: 'fish_1_frame_4', path: '/assets/tilesets/ocean_decor/fish_1/fish_1_frame_4.png' },
           { key: 'fish_2_frame_1', path: '/assets/tilesets/ocean_decor/fish_2/fish_2_frame_1.png' },
           { key: 'fish_2_frame_2', path: '/assets/tilesets/ocean_decor/fish_2/fish_2_frame_2.png' },
           { key: 'fish_2_frame_3', path: '/assets/tilesets/ocean_decor/fish_2/fish_2_frame_3.png' },
           { key: 'fish_2_frame_4', path: '/assets/tilesets/ocean_decor/fish_2/fish_2_frame_4.png' },
         ],
       },
       {
        category: 'island_decor',
        tiles: [
          { key: 'island_decor_01', path: '/assets/tilesets/island/decor/island_decor_01.png' },
          { key: 'island_decor_02', path: '/assets/tilesets/island/decor/island_decor_02.png' },
          { key: 'island_decor_03', path: '/assets/tilesets/island/decor/island_decor_03.png' },
          { key: 'island_decor_04', path: '/assets/tilesets/island/decor/island_decor_04.png' },
          { key: 'island_decor_05', path: '/assets/tilesets/island/decor/island_decor_05.png' },
          { key: 'island_decor_06', path: '/assets/tilesets/island/decor/island_decor_06.png' },
          { key: 'island_decor_07', path: '/assets/tilesets/island/decor/island_decor_07.png' },
          { key: 'island_decor_08', path: '/assets/tilesets/island/decor/island_decor_08.png' },
        ],
      },
    ];

     tilesets.forEach(tileset => {
       tileset.tiles.forEach(tile => {
         if (tile.variants) {
           // Special handling for 0-indexed animation frames (ocean_animation_frame_0 through _4)
           if (tile.key === 'ocean_animation') {
             for (let i = 0; i < tile.variants; i++) {
               const textureKey = `${tile.key}_${i}`;
               const texturePath = `${tile.path}${i}.png`;
               this.scene.load.image(textureKey, texturePath);
               this.loadedTextures.add(textureKey);
             }
           } else {
             // Standard 1-indexed variants
             for (let i = 1; i <= tile.variants; i++) {
               const textureKey = `${tile.key}_${i}`;
               const texturePath = `${tile.path}_${i}.png`;
               this.scene.load.image(textureKey, texturePath);
               this.loadedTextures.add(textureKey);
             }
           }
           this.tileVariants.set(tile.key, tile.variants);
           console.log(`  📦 Loaded ${tile.key} with ${tile.variants} variants`);
         } else {
           this.scene.load.image(tile.key, tile.path);
           this.loadedTextures.add(tile.key);
           console.log(`  📦 Loaded ${tile.key}`);
         }
       });
    });

    console.log(`✅ TilesetManager: Preloaded ${this.loadedTextures.size} textures`);
  }

   public getTileTexture(tileType: string, variant?: number): string | null {
     const variantCount = this.tileVariants.get(tileType);
     
     if (variantCount) {
       // Special handling for 0-indexed animation frames
       if (tileType === 'ocean_animation') {
         if (variant !== undefined && variant >= 0 && variant < variantCount) {
           const textureKey = `${tileType}_${variant}`;
           return this.hasTexture(textureKey) ? textureKey : null;
         }
         const randomVariant = Math.floor(Math.random() * variantCount);
         const textureKey = `${tileType}_${randomVariant}`;
         return this.hasTexture(textureKey) ? textureKey : null;
       }
       // Standard 1-indexed variants
       if (variant !== undefined && variant >= 1 && variant <= variantCount) {
         const textureKey = `${tileType}_${variant}`;
         return this.hasTexture(textureKey) ? textureKey : null;
       }
       const randomVariant = Math.floor(Math.random() * variantCount) + 1;
       const textureKey = `${tileType}_${randomVariant}`;
       return this.hasTexture(textureKey) ? textureKey : null;
     }

     return this.hasTexture(tileType) ? tileType : null;
   }

  public getRandomVariant(tileType: string): string | null {
    const variantCount = this.tileVariants.get(tileType);
    
    if (variantCount) {
      const randomVariant = Math.floor(Math.random() * variantCount) + 1;
      const textureKey = `${tileType}_${randomVariant}`;
      return this.hasTexture(textureKey) ? textureKey : null;
    }

    return this.hasTexture(tileType) ? tileType : null;
  }

   public hasTileTexture(tileType: string): boolean {
    if (this.tileVariants.has(tileType)) {
      const variantCount = this.tileVariants.get(tileType)!;
      for (let i = 1; i <= variantCount; i++) {
        if (this.hasTexture(`${tileType}_${i}`)) {
          return true;
        }
      }
      console.warn(`🎨 No variants found for ${tileType} (expected ${variantCount} variants)`);
      return false;
    }

    const exists = this.hasTexture(tileType);
    if (!exists) {
      console.warn(`🎨 Texture not found: ${tileType}`);
    }
    return exists;
  }

  public debugLogTextures(): void {
    console.log('🔍 TilesetManager Debug Info:');
    console.log(`  Total tracked textures: ${this.loadedTextures.size}`);
    console.log(`  Tracked texture keys: ${Array.from(this.loadedTextures).join(', ')}`);
    console.log(`  Tile variants: ${JSON.stringify(Object.fromEntries(this.tileVariants))}`);
    
    console.log('  Checking actual loaded textures:');
    this.loadedTextures.forEach(key => {
      const exists = this.hasTexture(key);
      console.log(`    ${key}: ${exists ? '✅' : '❌'}`);
    });
  }

  public getVariantCount(tileType: string): number {
    return this.tileVariants.get(tileType) || 0;
  }

  private hasTexture(textureKey: string): boolean {
    return this.scene.textures.exists(textureKey);
  }

  /**
   * Check if a specific texture key exists (for non-variant textures like island decor)
   */
  public hasDirectTexture(textureKey: string): boolean {
    return this.hasTexture(textureKey);
  }

  public getLoadedTextureCount(): number {
    return this.loadedTextures.size;
  }

  public getLoadedTextures(): string[] {
    return Array.from(this.loadedTextures);
  }
}
